import { createFileRoute, Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { PageHeader } from "@/client/components/page-header";
import { apiQuery, useApiSuspenseQuery } from "@/client/lib/api";
import { ensurePage, pageHead } from "@/client/lib/page";
import { CampaignClient } from "@/client/modules/campanhas/campanhas-client";
import Loading from "@/client/modules/campanhas/loading";
import { BIRTHDAY_TEMPLATE_DEFAULT, REACTIVATION_TEMPLATE_DEFAULT } from "@/shared/modules/campanhas/lib/templates";
import { cn } from "@/shared/utils";

type Search = { aba?: string; meses?: string };

export const Route = createFileRoute("/app/campanhas")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    aba: s.aba as string | undefined,
    meses: s.meses as string | undefined,
  }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => ensurePage(context.queryClient, apiQuery("campanhas", "page", deps)),
  head: () => pageHead("Aniversários e campanhas"),
  pendingComponent: Loading,
  component: CampanhasPage,
});

function TabLink({ search, active, children }: { search: Search; active: boolean; children: ReactNode }) {
  return (
    <Link
      to="/app/campanhas"
      search={search}
      resetScroll={false}
      className={cn(
        "flex-1 whitespace-nowrap rounded-lg px-3 py-2 text-center text-sm font-medium transition",
        active ? "bg-brand-700 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100",
      )}
    >
      {children}
    </Link>
  );
}

function CampanhasPage() {
  const search = Route.useSearch();
  const { data } = useApiSuspenseQuery("campanhas", "page", search);
  const { aba, meses } = data;
  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6">
      <PageHeader
        title="Aniversários e campanhas"
        description="Uma lembrança no aniversário e um empurrãozinho pra quem sumiu — cada um com a mensagem pronta pro WhatsApp."
      />
      <div className="flex gap-1 rounded-xl border border-slate-200 bg-white p-1 shadow-soft">
        <TabLink search={{ aba: "aniversarios" }} active={aba === "aniversarios"}>
          🎂 Aniversariantes
        </TabLink>
        <TabLink search={{ aba: "reativacao", meses: String(meses) }} active={aba === "reativacao"}>
          👋 Reativação
        </TabLink>
      </div>
      {aba === "aniversarios" ? (
        <div className="flex flex-col gap-3">
          <p className="px-1 text-xs text-slate-500">Faz aniversário hoje ou nos próximos 6 dias.</p>
          <CampaignClient
            rows={data.rows}
            defaultTemplate={BIRTHDAY_TEMPLATE_DEFAULT}
            emptyLabel="Ninguém faz aniversário nos próximos 7 dias."
          />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2 px-1">
            <span className="text-xs text-slate-500">Sem visita há mais de:</span>
            <div className="flex gap-1 rounded-lg border border-slate-200 bg-white p-0.5 shadow-soft">
              {data.windows.map((w) => (
                <Link
                  key={w}
                  to="/app/campanhas"
                  search={{ aba: "reativacao", meses: String(w) }}
                  resetScroll={false}
                  className={cn(
                    "rounded-md px-2.5 py-1 text-xs font-medium transition",
                    meses === w ? "bg-brand-700 text-white" : "text-slate-600 hover:bg-slate-100",
                  )}
                >
                  {w === 12 ? "1 ano" : w === 24 ? "2 anos" : `${w} meses`}
                </Link>
              ))}
            </div>
          </div>
          <CampaignClient
            rows={data.rows}
            defaultTemplate={REACTIVATION_TEMPLATE_DEFAULT}
            emptyLabel="Ninguém está sem visita nesse intervalo (ou todos já têm retorno marcado)."
            notice={
              data.truncated
                ? "Mostrando os 200 mais antigos. Estreite o período pra ver menos de cada vez."
                : undefined
            }
          />
        </div>
      )}
    </div>
  );
}
